import { useEffect, useMemo, useRef, useState } from "react";
import {
  IconAddressBook,
  IconBell,
  IconSettings as SettingsIcon,
  IconUser,
} from "@tabler/icons-react";
import { SupportErrorMessage } from "../../components/overlays/EngineeringSupportBanner";
import { domainMutationClient } from "../../api/domainMutationClient";
import { domainRouterClient } from "../../api/domainRouterClient";
import { useShellProfile } from "../../state/useShellProfile";
import {
  useGetApplicantApplicationsQuery,
  useListNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from "../../state/domainRouterApi";
import {
  buildParentSettingsProfileDetails,
  EMPTY_PARENT_PROFILE_VALUE,
  resolveInitialParentNotificationCategories,
  resolveParentNotificationCategoryToggle,
  resolveParentNotificationMuteToggle,
  type ParentNotificationPreferenceInput,
  type ParentSettingsProfileInput,
} from "../portalSettingsProfileDetails";

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-irongrey/50 font-medium">{children}</p>
);

const ErrorState = ({ children }: { children: React.ReactNode }) => (
  <SupportErrorMessage>{children}</SupportErrorMessage>
);

const SettingsSection = ({
  title,
  icon: Icon,
  children,
  className = "",
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  className?: string;
}) => (
  <section
    className={`bg-white border border-gray-100 rounded-3xl p-8 shadow-card ${className}`.trim()}
  >
    <header className="flex items-center gap-3 mb-6">
      <Icon size={22} className="text-irongrey/80" aria-hidden />
      <h2 className="text-lg font-bold text-irongrey">{title}</h2>
    </header>
    {children}
  </section>
);

const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
  testId,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  testId?: string;
}) => (
  <button
    type="button"
    aria-pressed={checked}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className="w-full text-left flex items-start justify-between gap-4 py-5"
  >
    <article className="min-w-0">
      <h3 className="text-base font-bold text-irongrey leading-tight">
        {label}
      </h3>
      <p className="text-xs text-irongrey/40 mt-1 font-medium leading-relaxed">
        {description}
      </p>
    </article>
    <span
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
        checked ? "bg-aquamarine border-aquamarine" : "border-gray-200 bg-white"
      }`}
      aria-hidden
    >
      {checked ? <span className="w-2.5 h-2.5 rounded-sm bg-white" /> : null}
    </span>
  </button>
);

const ProfileField = ({
  label,
  value,
  testId,
  wide = false,
}: {
  label: string;
  value: string;
  testId: string;
  wide?: boolean;
}) => (
  <li className={wide ? 'md:col-span-2' : undefined}>
    <p className="text-xs font-bold text-irongrey/60 uppercase tracking-widest mb-2.5">{label}</p>
    <p
      className={`min-h-[46px] px-4 py-3 rounded-xl border border-gray-100 bg-white text-sm font-semibold text-irongrey ${
        value === EMPTY_PARENT_PROFILE_VALUE ? 'text-irongrey/45' : ''
      }`}
      data-testid={testId}
    >
      {value}
    </p>
  </li>
);

type PreferredContactMethod = "email" | "phone" | "sms" | "portal";

type ContactPreferenceState = "idle" | "loading" | "loaded" | "error";
type SaveState = "idle" | "saving" | "saved";

const DEFAULT_CONTACT_METHOD: PreferredContactMethod = "email";
const CONTACT_METHODS: Array<{
  value: PreferredContactMethod;
  label: string;
  description: string;
}> = [
  {
    value: "email",
    label: "Email",
    description: "Use the email address on my parent profile.",
  },
  {
    value: "phone",
    label: "Phone call",
    description: "Call the phone number the school has on file.",
  },
  {
    value: "sms",
    label: "Text message",
    description: "Send short updates by text when appropriate.",
  },
  {
    value: "portal",
    label: "Portal message",
    description: "Use messages inside the parent portal first.",
  },
];
const CONTACT_METHOD_VALUES = new Set(
  CONTACT_METHODS.map((method) => method.value),
);

const normalizePreferredContactMethod = (
  value: unknown,
  fallback: PreferredContactMethod = DEFAULT_CONTACT_METHOD,
): PreferredContactMethod => {
  const candidate = String(value ?? "")
    .trim()
    .toLowerCase();
  return CONTACT_METHOD_VALUES.has(candidate as PreferredContactMethod)
    ? (candidate as PreferredContactMethod)
    : fallback;
};

const readPreferredContactMethod = (
  preferences: unknown,
  fallback: PreferredContactMethod = DEFAULT_CONTACT_METHOD,
): PreferredContactMethod =>
  normalizePreferredContactMethod(
    (preferences as { preferredContactMethod?: unknown } | null)
      ?.preferredContactMethod,
    fallback,
  );

const readErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.trim().length > 0
    ? error.message
    : fallback;

type NotificationPreferenceRecord = {
  categoryFilters?: unknown;
  digestFrequency?: unknown;
  muted?: unknown;
  updatedAt?: unknown;
};

const readNotificationPreferenceUpdatedAt = (
  preference: NotificationPreferenceRecord | undefined,
): number | null => {
  const value = Number(preference?.updatedAt);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export type PortalSettingsStorybookFixture = {
  effectivePersona?: "parent";
  profile: ParentSettingsProfileInput & {
    userSub: string;
    email: string;
  };
  application?: Record<string, unknown>;
  notificationPreference?: ParentNotificationPreferenceInput;
  userPreference?: {
    preferredContactMethod?: string | null;
  };
};

export function PortalSettingsPage({
  storybookFixture,
}: {
  storybookFixture?: PortalSettingsStorybookFixture;
}) {
  const shellProfile = useShellProfile();
  const profile = storybookFixture?.profile ?? shellProfile.profile;
  const effectivePersona =
    storybookFixture?.effectivePersona ?? shellProfile.effectivePersona;
  const actorId = profile?.userSub ?? null;
  const role = "parent";
  const storybookContactMethod =
    storybookFixture?.userPreference?.preferredContactMethod;

  const preferencesQuery = useListNotificationPreferencesQuery(
    actorId ? { actorId, role } : {},
    { skip: !actorId || Boolean(storybookFixture) },
  );
  const applicationsQuery = useGetApplicantApplicationsQuery(
    { applicantId: actorId ?? '', limit: 25 },
    { skip: !actorId || Boolean(storybookFixture) },
  );
  const [updatePreferences, updateState] =
    useUpdateNotificationPreferencesMutation();
  const [storybookSaveState, setStorybookSaveState] = useState<
    "idle" | "saving" | "saved"
  >("idle");
  const [notificationSaveState, setNotificationSaveState] =
    useState<SaveState>("idle");
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [contactPreferenceState, setContactPreferenceState] =
    useState<ContactPreferenceState>(storybookFixture ? "loaded" : "idle");
  const [contactMethod, setContactMethod] = useState<PreferredContactMethod>(
    () => normalizePreferredContactMethod(storybookContactMethod),
  );
  const [initialContactMethod, setInitialContactMethod] =
    useState<PreferredContactMethod>(contactMethod);
  const [contactSaveState, setContactSaveState] = useState<SaveState>("idle");
  const [contactLoadError, setContactLoadError] = useState<string | null>(null);
  const [contactSaveError, setContactSaveError] = useState<string | null>(null);
  const latestSavedNotificationPreferenceAt = useRef<number | null>(null);

  const current: NotificationPreferenceRecord | undefined = storybookFixture
    ? storybookFixture.notificationPreference
    : (preferencesQuery.data?.items?.[0] as NotificationPreferenceRecord | undefined);

  const initialCategoryFilters = useMemo(() => {
    return resolveInitialParentNotificationCategories(current);
  }, [current?.categoryFilters, current?.muted]);

  const applicationRecord = useMemo(() => {
    if (storybookFixture?.application) return storybookFixture.application;
    return (applicationsQuery.data?.items?.[0] ?? null) as Record<string, unknown> | null;
  }, [applicationsQuery.data?.items, storybookFixture?.application]);

  const profileDetails = useMemo(() => buildParentSettingsProfileDetails({
    profile,
    application: applicationRecord,
  }), [applicationRecord, profile]);

  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(
    initialCategoryFilters,
  );
  const [muted, setMuted] = useState<boolean>(Boolean(current?.muted));
  const [digestFrequency, setDigestFrequency] = useState<string>(
    String(current?.digestFrequency ?? "daily"),
  );

  const applyNotificationPreferenceState = (
    preference: NotificationPreferenceRecord | undefined,
  ) => {
    setMuted(Boolean(preference?.muted));
    setDigestFrequency(String(preference?.digestFrequency ?? "daily"));
    setCategoryFilters(resolveInitialParentNotificationCategories(preference));
  };

  // Keep local state in sync when preference records arrive, without letting
  // eventually-consistent stale refetches roll back a successful save.
  const hasLoaded = Boolean(storybookFixture) || preferencesQuery.isSuccess;
  useEffect(() => {
    if (!hasLoaded) return;
    const currentUpdatedAt = readNotificationPreferenceUpdatedAt(
      current as NotificationPreferenceRecord | undefined,
    );
    const latestSavedAt = latestSavedNotificationPreferenceAt.current;
    if (latestSavedAt && currentUpdatedAt && currentUpdatedAt < latestSavedAt) {
      return;
    }
    applyNotificationPreferenceState(
      current as NotificationPreferenceRecord | undefined,
    );
  }, [
    current?.categoryFilters,
    current?.digestFrequency,
    current?.muted,
    current?.updatedAt,
    hasLoaded,
  ]);

  useEffect(() => {
    if (storybookFixture) {
      const initialMethod = normalizePreferredContactMethod(
        storybookContactMethod,
      );
      setContactPreferenceState("loaded");
      setContactLoadError(null);
      setContactMethod(initialMethod);
      setInitialContactMethod(initialMethod);
      return;
    }

    let cancelled = false;
    if (!actorId) {
      setContactPreferenceState("idle");
      setContactLoadError(null);
      return () => {
        cancelled = true;
      };
    }

    setContactPreferenceState("loading");
    setContactLoadError(null);
    domainRouterClient
      .query("getUserPreferences", {})
      .then((preferences) => {
        if (cancelled) return;
        const initialMethod = readPreferredContactMethod(preferences);
        setContactMethod(initialMethod);
        setInitialContactMethod(initialMethod);
        setContactPreferenceState("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setContactPreferenceState("error");
        setContactLoadError("Unable to load contact preferences.");
      });

    return () => {
      cancelled = true;
    };
  }, [actorId, storybookContactMethod, storybookFixture]);

  const clearSaveFeedback = () => {
    setSaveSucceeded(false);
    setSaveError(null);
    if (storybookSaveState === "saved") setStorybookSaveState("idle");
    if (notificationSaveState === "saved") setNotificationSaveState("idle");
  };

  const clearContactSaveFeedback = () => {
    setContactSaveError(null);
    if (contactSaveState === "saved") setContactSaveState("idle");
  };

  const toggleCategory = (category: string, next: boolean) => {
    clearSaveFeedback();
    const result = resolveParentNotificationCategoryToggle(categoryFilters, muted, category, next);
    setCategoryFilters(result.categoryFilters);
    setMuted(result.muted);
  };

  const toggleMuted = (next: boolean) => {
    clearSaveFeedback();
    const result = resolveParentNotificationMuteToggle(categoryFilters, next);
    setCategoryFilters(result.categoryFilters);
    setMuted(result.muted);
  };

  const isSavingNotificationPreferences =
    notificationSaveState === "saving" ||
    updateState.isLoading ||
    storybookSaveState === "saving";

  const handleSave = async (): Promise<void> => {
    if (!actorId || isSavingNotificationPreferences) return;

    setSaveSucceeded(false);
    setSaveError(null);
    setNotificationSaveState("saving");
    let saveCompleted = false;

    try {
      if (storybookFixture) {
        setStorybookSaveState("saving");
        await Promise.resolve();
        setStorybookSaveState("saved");
        setNotificationSaveState("saved");
        setSaveSucceeded(true);
        saveCompleted = true;
        return;
      }

      const notificationPreferencePayload = {
        actorId,
        role,
        categoryFilters: Array.from(categoryFilters.values()),
        muted,
        digestFrequency,
      };
      const mutationResult = updatePreferences(notificationPreferencePayload);
      const savedPreference =
        (await mutationResult.unwrap()) as NotificationPreferenceRecord;
      latestSavedNotificationPreferenceAt.current =
        readNotificationPreferenceUpdatedAt(savedPreference) ?? Date.now();
      applyNotificationPreferenceState(savedPreference);

      setNotificationSaveState("saved");
      setSaveSucceeded(true);
      saveCompleted = true;

      try {
        await preferencesQuery.refetch();
      } catch {
        setSaveError(
          "Notification preferences were saved, but the latest settings could not be refreshed.",
        );
      }
    } catch (error) {
      setSaveSucceeded(false);
      setStorybookSaveState("idle");
      setSaveError(
        readErrorMessage(error, "Unable to save notification preferences."),
      );
    } finally {
      if (!saveCompleted) {
        setNotificationSaveState("idle");
      }
    }
  };

  const handleContactMethodChange = (nextMethod: string) => {
    clearContactSaveFeedback();
    if (!CONTACT_METHOD_VALUES.has(nextMethod as PreferredContactMethod)) {
      setContactSaveError("Choose a valid contact method.");
      return;
    }
    setContactMethod(nextMethod as PreferredContactMethod);
  };

  const handleContactSave = async () => {
    if (!actorId) return;
    const preferredContactMethod =
      normalizePreferredContactMethod(contactMethod);
    setContactSaveError(null);
    setContactLoadError(null);
    setContactSaveState("saving");

    if (storybookFixture) {
      await Promise.resolve();
      setInitialContactMethod(preferredContactMethod);
      setContactPreferenceState("loaded");
      setContactSaveState("saved");
      return;
    }

    try {
      const savedPreferences = await domainMutationClient.updateUserPreferences(
        {
          preferredContactMethod,
        },
      );
      const savedMethod = readPreferredContactMethod(
        savedPreferences,
        preferredContactMethod,
      );
      setContactMethod(savedMethod);
      setInitialContactMethod(savedMethod);
      setContactPreferenceState("loaded");
      setContactLoadError(null);
      setContactSaveState("saved");

      try {
        const reloadedPreferences = await domainRouterClient.query(
          "getUserPreferences",
          {},
        );
        const reloadedMethod = readPreferredContactMethod(
          reloadedPreferences,
          savedMethod,
        );
        setContactMethod(reloadedMethod);
        setInitialContactMethod(reloadedMethod);
        setContactPreferenceState("loaded");
        setContactLoadError(null);
      } catch {
        setContactSaveError(
          "Contact preference was saved, but the latest setting could not be refreshed.",
        );
      }
    } catch (error) {
      setContactSaveState("idle");
      setContactSaveError(
        readErrorMessage(error, "Unable to save contact preferences."),
      );
    }
  };

  const isSavingContactPreferences = contactSaveState === "saving";
  const isLoadingContactPreferences = contactPreferenceState === "loading";
  const contactPreferenceIsDirty = contactMethod !== initialContactMethod;

  return (
    <main
      data-testid="parent-portal-settings"
      data-extracted-from="owned:ystnj/parent-portal/settings"
      data-effective-persona={effectivePersona}
      className="w-full space-y-6 pb-20"
    >
      <header className="flex items-center gap-3">
        <span
          className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center"
          aria-hidden
        >
          <SettingsIcon size={20} className="text-primary" />
        </span>
        <section>
          <h1 className="text-2xl font-bold text-irongrey tracking-tight">
            Settings
          </h1>
          <p className="text-sm text-irongrey/60 font-medium mt-1">
            Manage your preferences.
          </p>
        </section>
      </header>

      {!actorId ? (
        <ErrorState>Unable to resolve your account identity.</ErrorState>
      ) : null}

      <div
        data-testid="parent-portal-settings-visual-surface"
        className="space-y-6"
      >
        <SettingsSection title="Profile" icon={IconUser}>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ProfileField
              label="Full name"
              value={profileDetails.fullName}
              testId="parent-portal-settings-profile-full-name"
            />
            <ProfileField
              label="Family name"
              value={profileDetails.familyName}
              testId="parent-portal-settings-profile-family-name"
            />
            <ProfileField
              label="Primary Contact"
              value={profileDetails.primaryContact}
              testId="parent-portal-settings-profile-primary-contact"
            />
            <ProfileField
              label="Email"
              value={profileDetails.email}
              testId="parent-portal-settings-profile-email"
            />
            <ProfileField
              label="Phone"
              value={profileDetails.phone}
              testId="parent-portal-settings-profile-phone"
            />
            <ProfileField
              label="Home Phone"
              value={profileDetails.homePhone}
              testId="parent-portal-settings-profile-home-phone"
            />
            <ProfileField
              label="Home Address"
              value={profileDetails.homeAddress}
              testId="parent-portal-settings-profile-home-address"
              wide
            />
          </ul>
          {applicationsQuery.error ? (
            <ErrorState>Unable to load family profile details.</ErrorState>
          ) : null}
        </SettingsSection>

        <SettingsSection
          title="Contact Preferences"
          icon={IconAddressBook}
          className="nodisplay"
        >
          {isLoadingContactPreferences ? (
            <Empty>Loading contact preferences...</Empty>
          ) : null}
          {contactLoadError ? (
            <ErrorState>{contactLoadError}</ErrorState>
          ) : null}

          <fieldset
            className="space-y-4"
            aria-describedby="parent-portal-settings-contact-method-help"
          >
            <legend className="text-xs font-bold text-irongrey/60 uppercase tracking-widest">
              Preferred Contact Method
            </legend>
            <p
              id="parent-portal-settings-contact-method-help"
              className="text-sm text-irongrey/50 font-medium leading-relaxed"
            >
              Choose how the school should contact you first for parent updates.
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {CONTACT_METHODS.map((method) => (
                <label
                  key={method.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${
                    contactMethod === method.value
                      ? "border-primary bg-primary/5"
                      : "border-gray-100 bg-white hover:border-primary/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="preferredContactMethod"
                    value={method.value}
                    checked={contactMethod === method.value}
                    required
                    onChange={(event) =>
                      handleContactMethodChange(event.currentTarget.value)
                    }
                    data-testid={`parent-portal-settings-contact-method-${method.value}`}
                    className="mt-1 h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-irongrey">
                      {method.label}
                    </span>
                    <span className="mt-1 block text-xs font-medium leading-relaxed text-irongrey/45">
                      {method.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <footer className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <button
              type="button"
              onClick={handleContactSave}
              disabled={
                !actorId ||
                isLoadingContactPreferences ||
                isSavingContactPreferences
              }
              data-testid="parent-portal-settings-contact-save-button"
              className={`px-6 py-3 rounded-xl font-bold text-sm transition-all active:scale-95 ${
                !actorId ||
                isLoadingContactPreferences ||
                isSavingContactPreferences
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "text-white hover:opacity-90"
              }`}
              style={
                !actorId ||
                isLoadingContactPreferences ||
                isSavingContactPreferences
                  ? {}
                  : { backgroundColor: "rgb(23, 207, 167)" }
              }
            >
              {isSavingContactPreferences
                ? "Saving..."
                : "Save Contact Preference"}
            </button>
            {contactSaveState === "saved" ? (
              <span
                className="text-sm font-semibold text-primary"
                data-testid="parent-portal-settings-contact-save-status"
              >
                Saved.
              </span>
            ) : null}
            {contactPreferenceIsDirty &&
            contactSaveState === "idle" &&
            !contactSaveError ? (
              <span className="text-sm font-semibold text-irongrey/50">
                Unsaved changes.
              </span>
            ) : null}
            {contactSaveError ? (
              <SupportErrorMessage
                className="md:max-w-md"
                data-testid="parent-portal-settings-contact-save-error"
              >
                {contactSaveError}
              </SupportErrorMessage>
            ) : null}
          </footer>
        </SettingsSection>

        <SettingsSection
          title="Notifications"
          icon={IconBell}
          className="nodisplay"
        >
          {preferencesQuery.isLoading ? (
            <Empty>Loading preferences...</Empty>
          ) : null}
          {preferencesQuery.error ? (
            <ErrorState>Unable to load notification preferences.</ErrorState>
          ) : null}

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-irongrey/60">
                Digest Frequency
              </span>
              <select
                value={digestFrequency}
                onChange={(event) => {
                  clearSaveFeedback();
                  setDigestFrequency(event.currentTarget.value);
                }}
                data-testid="parent-portal-settings-digest-frequency"
                className="block h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-irongrey shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </label>
          </div>

          <ul
            className="divide-y divide-gray-100"
            aria-label="Notification toggles"
          >
            <li>
              <ToggleRow
                label="Attendance Updates"
                description="Notify me about attendance-related updates."
                checked={categoryFilters.has('attendance')}
                onChange={(next) => toggleCategory('attendance', next)}
                testId="parent-portal-settings-notification-attendance"
              />
            </li>
            <li>
              <ToggleRow
                label="Academic Updates"
                description="Notify me about academic updates and report cards."
                checked={categoryFilters.has('academic')}
                onChange={(next) => toggleCategory('academic', next)}
                testId="parent-portal-settings-notification-academic"
              />
            </li>
            <li>
              <ToggleRow
                label="Announcements"
                description="Important news and school announcements."
                checked={categoryFilters.has('announcements')}
                onChange={(next) => toggleCategory('announcements', next)}
                testId="parent-portal-settings-notification-announcements"
              />
            </li>
            <li>
              <ToggleRow
                label="Mute All"
                description="Silence notifications across the portal."
                checked={muted}
                onChange={toggleMuted}
                testId="parent-portal-settings-notification-muted"
              />
            </li>
          </ul>

          <footer className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <button
              type="button"
              onClick={() => {
                void handleSave();
              }}
              disabled={!actorId || isSavingNotificationPreferences}
              data-testid="parent-portal-settings-save-button"
              className={`px-6 py-3 rounded-xl font-bold text-sm transition-all active:scale-95 ${
                !actorId || isSavingNotificationPreferences
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "text-white hover:opacity-90"
              }`}
              style={
                !actorId || isSavingNotificationPreferences
                  ? {}
                  : { backgroundColor: "rgb(23, 207, 167)" }
              }
            >
              {isSavingNotificationPreferences ? "Saving..." : "Save"}
            </button>
            {storybookSaveState === "saved" || saveSucceeded ? (
              <span
                className="text-sm font-semibold text-primary"
                data-testid="parent-portal-settings-save-status"
              >
                Saved.
              </span>
            ) : null}
            {saveError ? (
              <SupportErrorMessage
                className="md:max-w-md"
                data-testid="parent-portal-settings-save-error"
              >
                {saveError}
              </SupportErrorMessage>
            ) : null}
          </footer>
        </SettingsSection>
      </div>
    </main>
  );
}
